<template>
  <v-flex xs12>
    <v-card class="pt-3">
      <v-flex xs12 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-flex xs12 v-if="success">
        <v-alert :value="true" type="success">{{success}}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-layout row wrap align-center class="px-2 pb-3">
          <datetime
            :phrases="{ ok: $lang.actions.ok, cancel: $lang.actions.cancel}"
            v-model="fromDate"
            type="datetime"
            class="datetime-btn primary--text mx-3"
          />
          <datetime
            :phrases="{ ok: $lang.actions.ok, cancel: $lang.actions.cancel}"
            v-model="toDate"
            type="datetime"
            class="datetime-btn primary--text mx-3"
          />
          <v-autocomplete
            class="mx-3 min-w max-w"
            clearable
            :label="$lang.actions.selectCompany"
            :items="mappedCompanies"
            :loading="companyLoading"
            v-model="companyId"
            hide-details
          />
        </v-layout>
        <v-layout row wrap class="px-2">
          <v-flex xs12 sm6 md4 px-3>
            <v-select
              :label="$lang.labels.configUpdates"
              :items="configTypes"
              v-model="configType"
              @change="setConfigShort"
              required
              :rules="[v => !!v || `${$lang.errors.missingCfgUpdate}`]"
              clearable
            />
          </v-flex>
          <v-flex xs12 sm6 md4 lg3 px-3 v-if="configType === 'Planet Configuration Updates'">
            <v-select
              :label="$lang.labels.planetType"
              :items="planetTypes"
              v-model="planetType"
              clearable
            />
          </v-flex>
          <v-flex xs12 sm6 md4 lg3 px-3>
            <v-autocomplete
              :label="$lang.labels.status"
              :items="statuses"
              v-model="status"
              clearable
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-btn class="primary mx-4 mb-4" @click="showUpdates" :disabled="!valid">{{$lang.actions.showUpdates}}</v-btn>
          <v-spacer />
          <v-text-field
            v-if="show"
            class="min-w max-w mx-4"
            v-model="search"
            append-icon="search"
            :label="$lang.labels.search"
            hide-details
          />
        </v-layout>
      </v-form>
      <v-data-table
        v-if="show"
        :headers="headers"
        :items="configs"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="[50, 100, 250, 500, { text:'All' , value: -1 }]"
      >
        <v-progress-linear height="6" slot="progress" color="primary" indeterminate />
        <template slot="items" slot-scope="props">
          <td v-if="props.item.moonInsatanceName" class="min-w">{{ props.item.moonInsatanceName }}</td>
          <td v-if="props.item.planetInstanceName" class="min-w">{{ props.item.planetInstanceName }}</td>
          <td class="min-w">{{ props.item.companyName }}</td>
          <td class="min-w">{{ formatDate(props.item.createdOn) }}</td>
          <td class="min-w">{{ formatDate(props.item.synchedOn) }}</td>
          <td>{{ props.item.status }}</td>
          <td><v-btn class="primary mx-0" @click="showModal(props.index)">{{$lang.actions.view}}</v-btn></td>
        </template>
        <v-alert slot="no-results" :value="true" color="warning" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card>
    <v-dialog v-if="configUpdatesView" v-model="configUpdatesView" max-width="1200">
      <config-modal :id="configs[configIndex].id" :type="configTypeShort" @close="closeModal" @prev="showPrev" @next="showNext"/>
    </v-dialog>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      configModal: () => import('./ConfigUpdateView'),
    },
    data () {
      return {
        pagination: {
          sortBy: 'createdOn',
          descending: true,
        },
        err: '',
        success: '',
        search: '',
        valid: false,
        show: false,
        configType: 'Moon Configuration Updates',
        configTypeShort: null,
        planetType: null,
        companyId: null,
        status: null,
        fromDate: DateTime.local().minus({ hours: 4 }).toISO(),
        toDate: DateTime.local().plus({ hours: 2 }).toISO(),
        configTypes: ['Moon Configuration Updates', 'Planet Configuration Updates'],
        planetTypes: ['webshop'],
        statuses: ['created', 'fetched', 'synched', 'invalidate'],
        companyLoading: true,
        companies: [],
        loading: true,
        headers: [
          {text: this.$lang.header.instanceName, value: 'instanceName', align: 'left',},
          {text: this.$lang.labels.companyName, value: 'companyName', align: 'left',},
          {text: this.$lang.labels.created, value: 'createdOn', align: 'left',},
          {text: this.$lang.header.synched, value: 'synchedOn', align: 'left',},
          {text: this.$lang.labels.status, value: 'status', align: 'left',},
          {text: this.$lang.labels.actions, value: 'actions', align: 'left', sortable: false,},
        ],
        configs: [],
        configIndex: null,
        configUpdatesView: false,
      };
    },
    watch: {
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
      companyId(val){
        this.$cookie.set('companyedit', val, { expires: '8h' });
      }
    },
    computed: {
      mappedCompanies() {
        return this.companies.map(company => ({
          text: `${company.name} (${company.companyStatus})`,
          value: company.id,
        }));
      },
    },
    methods: {
      showModal(index) {
        this.setConfigShort();
        this.configIndex = index;
        this.configUpdatesView = true;
      },
      closeModal() {
        this.configUpdatesView = false;
        this.configIndex = null;
      },
      showPrev() {
        if (this.configIndex === 0) return;
        this.configIndex -= 1;
      },
      showNext() {
        if (this.configIndex === this.configs.length - 1) return;
        this.configIndex += 1;
      },
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm');
      },
      setConfigShort() {
        if (this.configType === 'Moon Configuration Updates')  {
          this.configTypeShort = 'moon';
        }
        if (this.configType === 'Planet Configuration Updates')  {
          this.configTypeShort = 'planet';
        }
      },
      showUpdates() {
        this.show = true;
        this.loading = true;
        this.configs = [];
        if(this.fromDate === null) {
          this.err = 'Please select From Date'
        }
         if(this.toDate === null) {
          this.err = 'Please select To Date'
        }
        const data = {
          configType: this.configType,
          planetType: this.planetType,
          companyId: this.companyId,
          status: this.status,
          fromDate: this.fromDate,
          toDate: this.toDate,
        }
        const url = `${data.configType === 'Planet Configuration Updates' ? 'planet' : 'moon'}/configupdates/search`;
        this.$http.post(url, data)
          .then((res) => {
            if(res.body.status === 'Success') {
              this.loading = false;
              this.configs = res.body.configs;
              localStorage.setItem('configUpdates', JSON.stringify(data));
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.loading = false;
            this.err = err.body.statusText || err.body.message;
          })
      },
    },
    created() {
      this.$http.get('company')
        .then((res) => {
          this.companyLoading = false;
          if (res.body.status === 'Success') {
            this.companies = res.body.companies;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.companyLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
      const edit = this.$cookie.get('companyedit');
      if (edit) this.companyId = edit;
      const prevConfig = localStorage.getItem('configUpdates');
      if (!prevConfig) return;
      const data = JSON.parse(prevConfig);
      this.configType = data.configType;
      this.setConfigShort();
      this.planetType = data.planetType;
      this.companyId = data.companyId;
      this.status = data.status;
      this.fromDate = data.fromDate;
      this.toDate= data.toDate;
    },
    beforeDestroy() {
      const data = {
        configType: this.configType,
        planetType: this.planetType,
        companyId: this.companyId,
        status: this.status,
        fromDate: this.fromDate,
        toDate: this.toDate,
      };
      localStorage.setItem('configUpdates', JSON.stringify(data));
    },
  };
</script>

<style lang="scss" scoped>
  p {
    font-size: 16px;
    color: rgba(#000, 0.75);
  }
</style>



// WEBPACK FOOTER //
// ConfigUpdates.vue?4f0d36a0